import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { dialogKeys } from "../../const/URL.const"
import { PrintersForm } from "../../formSchemas/printers.form"
import { IReduxAppState } from "../../reducers"
import { HTTPMethods } from "../../yoello-lib/modules/API/API"
import IDynamicFormRow from "../../yoello-lib/modules/dynamicForms/interfaces/IDynamicFormRow";
import { useDialogSetup } from "../dialogRoutes/useDialogSetup"
import { FilterTypes } from "./util"
import { IPrintersDialog } from "./PrinterDialog"
import { IPrinterFormState } from "./types";
import { isEqual } from "lodash"
import { setMultiDialogChanged } from "../../actions/dialog"
import { useSnackbarMessages } from "../../yoello-lib/modules/components/snackbar/SnackbarContext"





export function usePrinterDialog(props: IPrintersDialog) {
  const [state , setState] = useState<IPrinterFormState | undefined>(undefined);
  const [isLoading , setIsLoading] = useState<boolean>(false);

  const hasChanged = useSelector((state: IReduxAppState) => state.dialog.hasChanged);
  const venueID = useSelector((state: IReduxAppState) => state.selectedVenueId);

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { sendSnackbarMessage } = useSnackbarMessages()!;

  const { 
    goToNextStep, 
    goToPrevStep, 
    setProps, 
    getProps, 
    isLoading: isSettingUp, 
    closeAllModals,
  } = useDialogSetup(props,props.printerCallApi,"printer");


  const printerPrevData = useMemo(()=>{
    let oldData = {
      enabled : props?.printer?.enabled!== undefined ?props?.printer?.enabled:true,
      mac_address : props?.printer?.mac_address??'',
      name_public:  props?.printer?.name_public??'',
      MENU:[],
      SUBCATEGORY:[],
      VENUE_AREA:[],
      COLLECTION_POINT:[],
      ORDER_TYPE:[],
    }
    props?.printer?.filters?.forEach((filter: any)=> {
      oldData = {
        ...oldData ,
        [filter?.filter_type]: [...filter?.metadata]
      }
    })
    return oldData;

  },[props.printer])

  const formRows:IDynamicFormRow[] = useMemo(()=>{
    if(!hasChanged && printerPrevData && state){
      dispatch(setMultiDialogChanged(!isEqual(printerPrevData , state)));
    }
    return [
      ...PrintersForm({
        ...props,
        setProps,
        getProps,
        goToNextStep,
        goToPrevStep,
        t,
        id: props.id,
        formData: {
          ...state as IPrinterFormState,
          enabled : props?.itemData?.enabled??state?.enabled,
          mac_address : props?.itemData?.mac_address??state?.mac_address,
          name_public:props?.itemData?.name_public??state?.name_public,
        },
        isLoading: isSettingUp,
        callAPI: props.printerCallApi,
        closeAllModals,
        path: props.path,
        onDeleteFilter,
        setUpFilters
      }),
    ]; 
  // eslint-disable-next-line    
  },[state, props.itemData])

  useEffect(()=>{
    if (props.isNewItem){
      const newState = {
        name_public:props.itemData?.name_public??'',
        mac_address:props.itemData?.mac_address??'',
        enabled: props.itemData?.enabled!== undefined ?props.itemData?.enabled:true,
        MENU:props?.formState?.MENU??[],
        SUBCATEGORY:props?.formState?.SUBCATEGORY??[],
        VENUE_AREA:props?.formState?.VENUE_AREA??[],
        COLLECTION_POINT:props?.formState?.COLLECTION_POINT??[],
        ORDER_TYPE:props?.formState?.ORDER_TYPE??[],
      }
      setState(newState)
    }else{
      setPrinter();
    }
  // eslint-disable-next-line  
  }, [props.isNewItem , props?.itemData])

  function setPrinter(){
    let newState:IPrinterFormState = {
      enabled : props.itemData? props.itemData.enabled : printerPrevData.enabled,
      mac_address : props.itemData ? props.itemData?.mac_address : printerPrevData.mac_address,
      name_public: props.itemData ? props.itemData?.name_public : printerPrevData.name_public,
      MENU:[],
      SUBCATEGORY:[],
      VENUE_AREA:[],
      COLLECTION_POINT:[],
      ORDER_TYPE:[],
    }

    if (props?.formState) {
      Object.keys(FilterTypes).forEach((filter: any)=>{
        newState = {
          ...newState ,
          // @ts-ignore
          [filter]:props?.formState?.[filter] ?? []
        }
      }) 
    }else {
      newState = {
        ...printerPrevData
      }
    }
    setState(newState);
  }

  function setUpFilters(data: any[] , key:FilterTypes){
    setProps(dialogKeys.PRINTER, key , data , 'itemData');
    setProps(dialogKeys.PRINTER, 'formState' , {
      ...state!,
      [key]:data
    });
    setState({
      ...state!,
      [key]:data
    })
  }

  function onDeleteFilter(id: string, filterType: FilterTypes) {
    if(state && state[filterType].length > 0) {
      setUpFilters(state[filterType].filter((i: any)=> i.id !== id) , filterType)
    }
  }

  function filtersObjectGenerator() {
    let filters:any = [];
    Object.keys(FilterTypes).forEach((item)=>{
      if(state && state[item as FilterTypes].length > 0) {
        filters.push(
          {
            filter_type: item,
            values :  state[item as FilterTypes].map((i:any) => i.id),
            metadata: state[item as FilterTypes]
          }
        )
      }
    })
    return filters
  }

  function generateApiErrorMessage(error: any) {
    if (error?.response?.data?.data?.[0]?.code === "PRINTER_ALREADY_EXISTS") {
      sendSnackbarMessage(t('printerSettings.apiErrors.printerExists'), 'error')
    }
  }

  function onSubmit(data: any) {
    setIsLoading(true);
    const body = {
      name: data.name_public,
      mac_address: data.mac_address,
      enabled: data.enabled,
      venue_id: venueID,
      filters: filtersObjectGenerator(),
    }
    dispatch(setMultiDialogChanged(false));
    if(props.isNewItem) {
      props.printerCallApi(HTTPMethods.POST, {body}).then(async({isSuccessful, data})=>{
        
        if (!isSuccessful) {
          generateApiErrorMessage(data)
        }

        await props.printerCallApi(HTTPMethods.GET);
        closeAllModals(props.path);
      })
    }else {
      props.printerCallApi(HTTPMethods.PUT, {query: props.id, body}).then(async()=>{
        await props.printerCallApi(HTTPMethods.GET)
        closeAllModals(props.path);
      })
    }
  }
  return {
    formRows,
    isLoading,
    onSubmit,
    isSettingUp,
    formData:state,
    closeAllModals,
  }
}